import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  InputAdornment,
  Fade,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { login } from "../services/api/adminService";
import logoSvg from "../assets/TowHelp logo-07.svg";
import mockup from "../assets/Mobile.png";

const Login = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await login(credentials.email, credentials.password);
      localStorage.setItem("token", res.token);
      localStorage.setItem("refreshToken", res.refreshToken);
      localStorage.setItem("role", res.roles?.[0] || "");
      localStorage.setItem("userId", res.id);
      localStorage.setItem(
        "user",
        JSON.stringify({
          id: res.id,
          email: res.email,
          userName: res.userName,
          displayName: res.displayName,
          phoneNumber: res.phoneNumber,
          roles: res.roles,
        })
      );
      localStorage.setItem("isAdmin", "true");
      if (res.roles[0]) {
        localStorage.setItem("role", res.roles[0]);
      }
      if (res.roles?.includes("Owner")) {
        navigate("/dashboard/BookingsAdminScreen");
      } else {
        setError("Access denied: insufficient permissions");
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || "Invalid email or password";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", fontFamily: "Poppins" }}>
      {!isMobile && (
        <Box
          sx={{
            flex: 1.5,
            background: "linear-gradient(to bottom right, #e0f2fe, #bae6fd)",
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: -1,
              width: "100%",
              height: "100%",
              background: "#fff",
              clipPath: "polygon(90% 0%, 100% 0%, 100% 100%, 80% 100%)",
              zIndex: 2,
            }}
          />
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              animation: "float 4s ease-in-out infinite",
              "@keyframes float": {
                "0%": { transform: "translateY(0px)" },
                "50%": { transform: "translateY(-12px)" },
                "100%": { transform: "translateY(0px)" },
              },
            }}
          >
            <img
              src={mockup}
              alt="TowHelp App Mockup"
              style={{
                height: 580,
                width: "auto",
                borderRadius: "40px",
              }}
            />
          </Box>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: "100vh",
          background: "#fff",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: 3,
        }}
      >
        <Fade in timeout={700}>
          <Box sx={{ width: "100%", maxWidth: 420 }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, sm: 5 },
                borderRadius: "20px",
                display: "flex",
                flexDirection: "column",
                gap: 3,
                background: "#ffffff",
                border: "1px solid #e0e0e0",
                boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
              }}
            >
              <Box textAlign="center" mb={1}>
                <Box mb={1}>
                  <img
                    src={logoSvg}
                    alt="TowHelp Logo"
                    style={{ height: 60 }}
                  />
                </Box>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ fontFamily: "Poppins", mb: 0.5, color: "#0f172a" }}
                >
                  Welcome Back
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: "Poppins", color: "#475569" }}
                >
                  Admin access only
                </Typography>
              </Box>

              <TextField
                label="Email"
                fullWidth
                variant="outlined"
                value={credentials.email}
                onChange={(e) =>
                  setCredentials((prev) => ({
                    ...prev,
                    email: e.target.value,
                  }))
                }
                InputProps={{
                  sx: {
                    fontFamily: "Poppins",
                    boxShadow: "0 0 0 2px rgba(0,0,0,0.05)",
                    borderRadius: "10px",
                    backgroundColor: "#fff",
                    color: "#0f172a",
                  },
                }}
                InputLabelProps={{
                  sx: { fontFamily: "Poppins", color: "#0f172a" },
                }}
              />

              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                fullWidth
                variant="outlined"
                value={credentials.password}
                onChange={(e) =>
                  setCredentials((prev) => ({
                    ...prev,
                    password: e.target.value,
                  }))
                }
                InputProps={{
                  sx: {
                    fontFamily: "Poppins",
                    boxShadow: "0 0 0 2px rgba(0,0,0,0.05)",
                    borderRadius: "10px",
                    backgroundColor: "#fff",
                    color: "#0f172a",
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: "#0f172a" }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  sx: { fontFamily: "Poppins", color: "#0f172a" },
                }}
              />

              {error && (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ fontFamily: "Poppins", mt: -1 }}
                >
                  {error}
                </Typography>
              )}

              <Button
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                onClick={handleLogin}
                sx={{
                  background: "#0f172a",
                  color: "#ffffff",
                  fontWeight: "bold",
                  fontSize: "1rem",
                  fontFamily: "Poppins",
                  textTransform: "none",
                  py: 1.5,
                  mt: 1,
                  borderRadius: "12px",
                  boxShadow: "0 4px 14px rgba(0, 0, 0, 0.08)",
                  "&:hover": {
                    background: "#1e293b",
                  },
                }}
              >
                {loading ? "Logging in..." : "Login"}
              </Button>
            </Paper>
          </Box>
        </Fade>
      </Box>
    </Box>
  );
};

export default Login;
